import * as React from 'react'
import { styled, Text, spacing } from '@thg-commerce/gravity-theme'

import { RadioInputProps } from '../types'

const HiddenRadio = styled.input`
  border: 0;
  position: absolute;
  z-index: -1;
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  white-space: nowrap;
  width: 1px;
`
interface StyledRadioButtonProps {
  className?: string
  disabled?: boolean
  selected?: boolean
  error?: string
}

const StyledRadioButton = styled.div<StyledRadioButtonProps>`
  display: inline-flex;
  box-sizing: border-box;
  width: ${spacing(3)};
  min-width: ${spacing(3)};
  height: ${spacing(3)};
  min-height: ${spacing(3)};
  border-radius: 50%;
  border: 2px solid
    ${(props) =>
      !props.error
        ? !props.disabled
          ? props.selected
            ? props.theme.colors.palette.brand.base
            : props.theme.colors.palette.greys.grey
          : props.theme.colors.palette.greys.light
        : props.theme.colors.error.base};
`

interface StyledLabelProps {
  disabled?: boolean
}

const StyledLabel = styled.label<StyledLabelProps>`
  display: flex;
  justify-content: center;
  width: fit-content;
  cursor: pointer;
  color: ${(props) =>
    props.disabled
      ? props.theme.colors.palette.greys.light
      : props.theme.colors.palette.greys.dark};
  ${Text('bodyText', 'default')}
`

interface StyledContainersProps {
  disabled?: boolean
  selected: boolean
  bordered?: boolean
}

const StyledContainer = styled.div<StyledContainersProps>`
  box-sizing: border-box;
  padding: ${spacing(1)};
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  justify-content: center;
  width: ${(props) => (props.bordered ? '100%' : 'fit-content')};

  pointer-events: ${(props) => (props.disabled ? 'none' : '')};

  border: 2px solid
    ${(props) =>
      props.bordered
        ? props.theme.colors.palette.greys.light
        : props.theme.colors.palette.greys.white};

  &:hover {
    background: ${(props) => props.theme.colors.palette.brand.lightest};
  }
  &:active {
    background: ${(props) => props.theme.colors.palette.brand.lighter};
  }
  &:focus-within {
    border: 2px solid ${(props) => props.theme.colors.palette.brand.base};
  }
  /* DO NOT GROUP WITH ABOVE BREAKS POLYFILL FOR MSOFT BROWSERS */
  &.focus-within {
    border: 2px solid ${(props) => props.theme.colors.palette.brand.base};
  }

  margin-bottom: ${(props) => props.bordered && spacing(2)};
`

interface StyledSvgProps {
  disabled?: boolean
  selected?: boolean
}

const StyledSvg = styled.svg<StyledSvgProps>`
  margin: auto;
  fill: ${(props) =>
    props.selected
      ? props.disabled
        ? props.theme.colors.palette.greys.light
        : props.theme.colors.palette.brand.base
      : 'none'};
`

interface SvgRadioButtonCircleProps {
  height: number
  disabled?: boolean
  selected?: boolean
}

const SvgRadioButtonCircle = (props: SvgRadioButtonCircleProps) => {
  return (
    <StyledSvg
      viewBox={`0 0 ${props.height} ${props.height}`}
      height={props.height}
      xmlns="http://www.w3.org/2000/svg"
      selected={!!props.selected}
      disabled={!!props.disabled}
    >
      <circle
        cx={props.height / 2}
        cy={props.height / 2}
        r={props.height / 2}
      />
    </StyledSvg>
  )
}

const RadioText = styled.span<{ disabled?: boolean }>`
  margin: ${() => `auto ${spacing(1)} auto ${spacing(2)}`};
  color: ${(props) =>
    props.disabled
      ? props.theme.colors.palette.greys.light
      : props.theme.colors.palette.greys.darker};
`
const generateTestId = (label): string =>
  label ? `-${label.toLowerCase().split(' ').join('-')}` : ''

/** @component */
export const RadioInput = (props: RadioInputProps) => {
  return (
    <StyledContainer
      bordered={props.bordered}
      className={props.className}
      selected={props.radioGroupState === props.value}
      onClick={() => props.setGroupState && props.setGroupState(props.value)}
      data-testid={`radio-input-container${generateTestId(props.label)}`}
    >
      <StyledLabel disabled={props.disabled}>
        <HiddenRadio
          tabIndex={
            props.radioGroupState !== ''
              ? props.radioGroupState !== props.value
                ? -1
                : 0
              : 0
          }
          disabled={props.disabled}
          name={props.name}
          type="radio"
          value={props.value}
          ref={props.focusref}
          checked={props.radioGroupState === props.value}
          onChange={() =>
            props.setGroupState && props.setGroupState(props.value)
          }
          data-testid={`radio-input-hidden${generateTestId(props.label)}`}
        />
        <StyledRadioButton
          selected={props.radioGroupState === props.value}
          disabled={props.disabled}
          error={props.error}
        >
          <SvgRadioButtonCircle
            selected={props.radioGroupState === props.value}
            height={12}
            disabled={props.disabled}
          />
        </StyledRadioButton>
        <RadioText disabled={props.disabled}>{props.label}</RadioText>
      </StyledLabel>
    </StyledContainer>
  )
}

export default RadioInput
